<template>
  <form @submit.prevent="submit" class="flex flex-col gap-5 pt-5 border-t">
    <p class="text-lg">
      {{
        $t("labels.generic", {
          pt: "Crie textos com lacunas e utilize @ para adicionar e definir quais palavras devem ser colocadas em cada espaço em branco.",
          en: "Create texts with gaps and use @ to add and define which words must be put on each blank space",
        })
      }}
    </p>
    <p class="font-bold text-lg">{{ "Item" }}</p>

    <TipTap v-if="tipTapIsReady" questionStoreType="dragAndDrop" />

    <div id="btn-action-add" class="flex justify-end">
      <Button
        v-if="gaps?.length !== 2"
        :label="$t('labels.generic', { pt: 'Adicionar lacuna', en: 'Add gap' })"
        icon="pi pi-plus"
        class="btn-simple w-fit"
        @click.prevent="addGap"
      />
    </div>

    <div v-for="(input, index) in gaps" :key="index">
      <div class="flex justify-between items-center gap-3">
        <i
          id="remove-gap-action"
          class="pi pi-minus mt-6 bg-h-warning-700 rounded-full p-1 text-white cursor-pointer hover:scale-90 transition-all mb-5"
          @click.prevent="removeGap(index)"
        />
        <div class="w-full flex flex-col gap-2">
          <p>
            {{
              $t("labels.generic", {
                pt: "Resposta da lacuna ",
                en: "Response for gap",
              }) +
              (index + 1)
            }}
          </p>
          <InputText
            v-model="input.name"
            :maxLength="200"
            :class="[
              {
                'p-invalid':
                  questionStore.v$Dialog?.gaps.$dirty &&
                  questionStore.v$Dialog.gaps.$each.$response.$data[index].name
                    .$invalid,
              },
            ]"
          />
          <Errors
            :errors="
              questionStore.v$Dialog?.gaps.$dirty
                ? questionStore.v$Dialog.gaps.$each.$response?.$errors[index]
                    ?.name
                : []
            "
            :actualLength="input.name.length"
            :maxLength="200"
          />
        </div>
      </div>
    </div>

    <div id="actions" class="flex justify-end items-center gap-3">
      <Button label="Cancelar" class="btn-outline" @click.prevent="onClose" />
      <Button label="Confirmar" class="btn-primary" type="submit" />
    </div>
  </form>
</template>

<script lang="ts">
import { required } from "@/locale/locale.config";
import { useEditorStore } from "@/store/editor";
import { useQuizQuestionsDragNDropStore } from "@/store/quiz-question-drag";
import useVuelidate from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeMount,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
} from "vue";
import { useToast } from "primevue/usetoast";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import { useI18n } from "vue-i18n";
import { v4 as uuidv4 } from "uuid";
import { invalid } from "moment";
import { useRoute } from "vue-router";
export class GapInput {
  constructor(
    public name: string = "",
    public id: string = "0",
    public itemGroup: number = 0
  ) {}
}

export default defineComponent({
  name: "FormItems",
  emits: ["onClose"],
  props: {
    isEditing: {
      type: Object as PropType<{ action: boolean; index: number }>,
    },
  },
  setup(props, { emit }) {
    const gaps = ref([] as GapInput[]);
    const { t } = useI18n();
    const toast = useToast();
    const form = ref({
      sentence: useEditorStore.content!,
      gaps: gaps.value,
    });
    const v$ = useVuelidate(
      {
        sentence: { required },
        gaps: {
          $each: helpers.forEach({
            name: {
              required: helpers.withMessage(
                t("validations.required"),
                required
              ),
            },
          }),
        },
      },
      form.value
    );
    const questionStore = useQuizQuestionsDragNDropStore;
    const editor = ref(useEditorStore.editor);
    const content = ref(useEditorStore.content);

    const tipTapIsReady = ref(false);
    function addGap(): void {
      if (gaps.value?.length >= 2) return;

      const newGap = new GapInput();
      newGap.id = uuidv4();
      newGap.itemGroup = props.isEditing?.index! ?? 0;
      gaps.value.push(newGap);
      useQuizQuestionsDragNDropStore.setGaps([...gaps.value]);
    }

    function removeGap(index: number): void {
      const gapToRemove = gaps.value[index];
      gaps.value.splice(index, 1);
      useQuizQuestionsDragNDropStore.setGaps([...gaps.value]);

      if (useEditorStore.editor) {
        const editorContent = useEditorStore.editor.getJSON();
        const mentionToRemove = findMentionByLabel(
          editorContent,
          gapToRemove.name
        );
        if (!mentionToRemove) return;
        useEditorStore.editor
          .chain()
          .focus()
          .deleteSelection()
          .command(({ tr }) => {
            const pos = findMentionNode(tr.doc, mentionToRemove.id);
            if (pos) {
              tr.delete(pos.from, pos.to);
              return true;
            }
            return false;
          })
          .run();
      }
    }

    function findMentionNode(
      doc: any,
      id: string | number
    ): { from: number; to: number } | null {
      let foundPos: { from: number; to: number } | null = null;

      doc.descendants((node: any, pos: number) => {
        if (node.type.name === "mention" && node.attrs.id === id) {
          foundPos = { from: pos, to: pos + node.nodeSize };
          return false;
        }
      });

      return foundPos;
    }

    function findMentionByLabel(content: any, label: string): any | null {
      if (typeof content === "object" && content !== null) {
        if (content.type === "mention" && content.attrs.label === label) {
          return content.attrs;
        }
        for (const key in content) {
          const result = findMentionByLabel(content[key], label);
          if (result) return result;
        }
      }
      return null;
    }

    function updateMention(gap: GapInput) {
      let pos;
      const nodeSearched = useEditorStore.editor?.chain().command(({ tr }) => {
        pos = findMentionNode(tr.doc, gap.id);
        return pos?.from ? true : false;
      });
      if (pos) {
        nodeSearched
          ?.insertContentAt({ from: pos?.from!, to: pos?.to! }, [
            {
              type: "mention",
              attrs: { label: gap?.name, id: gap?.id },
            },
          ])
          .run();
      }
    }
    function removeMention(gap: GapInput) {
      let pos;
      useEditorStore.editor
        ?.chain()
        .deleteSelection()
        .command(({ tr }) => {
          pos = findMentionNode(tr.doc, gap.id);
          if (pos?.from) {
            tr.delete(pos.from, pos.to);
            return true;
          }
          return false;
        })
        .run();
    }
    function mentionsWereUsed(): boolean {
      const usedAllMentions = gaps.value.every((gap) => {
        let matchesNode = false;
        useEditorStore.editor?.$doc.content.descendants((node: any) => {
          if (node.type.name === "mention" && node.attrs.id === gap.id) {
            matchesNode = true;
          }
        });
        return matchesNode;
      });

      return gaps.value.length > 0 && usedAllMentions;
    }
    function submit(): void {
      questionStore.v$Dialog.$touch();
      if (questionStore.v$Dialog.$invalid) return;
      if (!mentionsWereUsed()) {
        toast.add(ToastFactory.error(Messages.RESPONSE_NOT_ADDED));
        return;
      } else {
        if (props.isEditing?.action) {
          questionStore.items[props.isEditing?.index] = useEditorStore.editor;
          const indexResponses = questionStore.responses.findIndex(
            (response) => response.itemGroup === props.isEditing?.index
          );
          questionStore.responses = questionStore.responses.filter(
            (response) => response.itemGroup != props.isEditing?.index
          );

          questionStore.responses.splice(indexResponses, 0, ...gaps.value);
        } else {
          questionStore.setItems(useEditorStore.editor);
          questionStore.setResponses([...gaps.value]);
        }

        onClose();
      }
    }
    function configEdit(): void {
      if (props.isEditing?.action) {
        (useEditorStore.editor = questionStore.items[props.isEditing?.index]),
          Object.assign(useEditorStore, {
            content: questionStore.items[props.isEditing?.index].getHTML(),
          });

        questionStore.responses.forEach((response) => {
          if (response.itemGroup === props.isEditing?.index) {
            gaps.value.push({ ...response });
          }
        });
        questionStore.gaps = gaps.value;
      } else {
        questionStore.gaps.length = 0;
      }
    }

    function onClose(): void {
      questionStore.v$Dialog.$reset();
      emit("onClose");
    }

    watch(gaps.value, (val) => {
      val.forEach((gap) => {
        if (gap?.name.length > 0) {
          updateMention(gap);
        } else {
          removeMention(gap);
        }
      });
    });

    watch(useEditorStore, (val) => {
      editor.value = val.getEditor;
      content.value = val.content;
    });

    onMounted(async () => {
      configEdit();

      form.value.sentence = useEditorStore.content!;

      questionStore.setVDialog(v$);
      questionStore.setSentenceForm(form.value);
      await nextTick();
      tipTapIsReady.value = true;
    });

    return {
      v$,
      addGap,
      removeGap,
      submit,
      onClose,
      gaps,
      questionStore,
      editor,
      tipTapIsReady,
    };
  },
});
</script>
